import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { TitleService } from 'src/services/title.service';
import { AppNav } from 'src/routes/app-nav';
import { Confirmation, ConfirmationService, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { ApiResponse } from 'src/interfaces/apiresponse.interface';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { RuntimeStatus } from 'src/enums/runtime-status.enum';
import Helpers from 'src/shared/helpers';
import { QueryListRespDTO } from '@/classes/QueryRespDTO';
import { QueryService } from '@/services/query.service';

@Component({
  selector: 'app-monitor-query-page',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './status-monitor-page.component.html',
})
export class StatusMonitorPageComponent implements OnInit, OnDestroy {
  private destroyed = new Subject<any>();
  private helpers: Helpers;

  messageKey: string = 'statusMonitorPage';
  queryEntries: QueryListRespDTO[] = [];
  finalStatuses: string[] = [
    RuntimeStatus[RuntimeStatus.Completed],
    RuntimeStatus[RuntimeStatus.Terminated],
    RuntimeStatus[RuntimeStatus.Failed],
  ];
  loading: boolean = false;

  @ViewChild(Table) logEntryTable!: Table;

  constructor(
    private titleService: TitleService,
    private confirmationService: ConfirmationService,
    private translocoService: TranslocoService,
    private messageService: MessageService,
    private queryService: QueryService
  ) {
    this.helpers = Helpers.getInstance(this.translocoService);
  }

  ngOnInit(): void {
    this.titleService.setTitle(AppNav.StatusMonitor.title);
    this.loadQueryEntries();
  }

  loadQueryEntries(): void {
    this.loading = true;
    this.queryService.getQueries().subscribe({
      next: (response: QueryListRespDTO[]) => {
        this.loading = false;

        this.queryEntries = response;
      },
      error: (response: HttpErrorResponse) => {
        this.loading = false;
        this.helpers.showApiResponseMessage(
          response,
          this.messageService,
          this.messageKey
        );
      },
    });
  }

  refreshInstanceStatus() {
    this.loadQueryEntries();
    this.messageService.clear();
  }

  showTerminateConfirm(instanceId: string) {
    const confirmObj: Confirmation = {
      ...Helpers.getInstance(this.translocoService).getConfirmBase(
        'defaultConfirmWithCheckbox'
      ),
      header: this.translocoService.translate('confirm'),
      message: this.translocoService.translate('confirm_terminate_instance'),
      acceptVisible: true,
      rejectVisible: true,
      closeOnEscape: true,
      accept: async () => {
        await this.terminateInstance(instanceId);
      },
    };

    this.confirmationService.confirm(confirmObj);
  }

  terminateInstance(instanceId: string) {
    this.messageService.clear();

    this.queryService.terminateQuery(instanceId).subscribe({
      next: (response: ApiResponse) => {
        this.helpers.showApiResponseMessage(
          response,
          this.messageService,
          this.messageKey
        );
      },
      error: (response: HttpErrorResponse) => {
        this.helpers.showApiResponseMessage(
          response,
          this.messageService,
          this.messageKey
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next(undefined);
    this.destroyed.complete();
  }
}
